import type { MaybeValidProps } from "@core/domain/models/maybe-valid.ts";
import type { Unbrand } from "@core/domain/types/unbrand.type.ts";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const GraphValuesSchema = z.record(z.string(), z.number());

const GraphSchema = z
    .object({
        name: z.string().trim(),
        yAxisValues: GraphValuesSchema,
    })
    .brand<"Graph">();

export type GraphInputProps = z.input<typeof GraphSchema>;
export type GraphProps = z.output<typeof GraphSchema>;

export class Graph {
    private constructor(private readonly props: Readonly<GraphProps>) {}

    get name(): string {
        return this.props.name;
    }

    get yAxisValues(): Record<string, number> {
        return this.props.yAxisValues;
    }

    static validate(
        props: Readonly<MaybeValidProps<GraphInputProps>>,
    ): Result<GraphProps, z.ZodError<GraphProps>> {
        const result = GraphSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<GraphInputProps>>,
    ): Result<GraphProps, PartialValid<typeof Graph>> {
        const result = partialSafeParse(GraphSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<GraphInputProps>,
    ): Result<Graph, z.ZodError<GraphProps>> {
        return Graph.validate(props).map(
            (validatedProps) => new Graph(validatedProps),
        );
    }

    static createWithRecovery(
        props: GraphInputProps,
    ): Result<Graph, ModelError<typeof Graph>> {
        return Graph.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.name)
                    return new NonRecuperableModelError();

                const defaultProps: Unbrand<GraphProps> = {
                    name: "",
                    yAxisValues: {},
                };

                const recoveryProps: Unbrand<GraphProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof Graph>(
                    new Graph(recoveryProps as GraphProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new Graph(validatedProps));
    }
}

export interface GraphFilters {
    stationId: number;
    sensorIds: number[];
    from: number;
    to: number;
}
